import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Helmet } from 'react-helmet';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './NetSuiteEcommercePage.css';
import FAQSection from './FAQSection';

const NetSuiteEcommercePage = () => {
  const controls = useAnimation();
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [whyRef, whyInView] = useInView({ threshold: 0.1 });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.1 });
  const [benefitsRef, benefitsInView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    if (heroInView) controls.start("visible");
  }, [controls, heroInView]);

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const whyChooseItems = [
    "Mobile-optimized and cloud-based platform for modern ecommerce",
    "Integrates inventory, order management, CRM, and financials into one ecosystem",
    "Unified shopping experiences across online and offline channels",
    "Multilingual, multicurrency, and multi-brand support for global reach",
    "Centralized control over inventory, orders, and personalized experiences"
  ];

  const coreFeatures = [
    {
      title: "B2B & B2C Commerce",
      description: "Handle both business and consumer transactions with advanced pricing and personalization",
      icon: "bi-shop"
    },
    {
      title: "B2C E-Commerce",
      description: "Built for direct consumer sales with product management and checkout optimization",
      icon: "bi-cart"
    },
    {
      title: "Promotions & Campaigns",
      description: "Advanced marketing with coupon management, loyalty programs and targeted campaigns",
      icon: "bi-megaphone"
    },
    {
      title: "Global Store",
      description: "Multi-site management with localization and regional preferences",
      icon: "bi-globe"
    },
    {
      title: "Business Insight Data",
      description: "Real-time store insights for better decision making and growth tracking",
      icon: "bi-graph-up"
    },
    {
      title: "Design & Personalize",
      description: "Customize your store design and user experience for maximum conversions",
      icon: "bi-palette"
    }
  ];

  const benefits = [
    {
      title: "Comprehensive Integration",
      description: "NetSuite unifies order management, inventory, and CRM in one platform. This integration eliminates data silos.",
      icon: "bi-diagram-3"
    },
    {
      title: "Multi-Channel Proficiency",
      description: "Sell across online stores, marketplaces, and social media from one unified system.",
      icon: "bi-broadcast"
    },
    {
      title: "Personalized Storefronts",
      description: "Create branded, customizable storefronts tailored to your customer segments.",
      icon: "bi-shop"
    },
    {
      title: "Integrated Inventory Oversight",
      description: "Gain real-time inventory insights for efficient purchasing and stock control.",
      icon: "bi-boxes"
    },
    {
      title: "Efficient Order Processing",
      description: "Centralize and streamline orders to reduce errors and boost customer satisfaction.",
      icon: "bi-cart-check"
    },
    {
      title: "Integration with Marketing Solutions",
      description: "Automate and sync marketing efforts, offering through NetSuite's ecommerce capabilities.",
      icon: "bi-megaphone"
    }
  ];

  const whyChooseUs = [
    {
      title: "Expert NetSuite Solutions",
      description: "Our team delivers comprehensive NetSuite ecommerce implementations."
    },
    {
      title: "Beyond-Go-Live Support",
      description: "We ensure continuous assistance even after implementation completion."
    },
    {
      title: "SuiteCommerce Development & Customization",
      description: "Tailored solutions for performance-driven ecommerce storefronts."
    },
    {
      title: "Enhanced Customer Experience",
      description: "Deliver shopping experiences that build customer loyalty."
    },
    {
      title: "Transform Your Business",
      description: "Ready to explore the full potential of NetSuite for your E-commerce?"
    }
  ];




  return (
    <div className="ecommerce-page bg-white overflow-hidden">
      <Helmet>
        <title>NetSuite Ecommerce Solutions USA | SpireNest</title>
        <link rel="canonical" href="https://spirenest.co/netsuite/services/ecommerce" />
        <meta name="description" content="Power your online business with NetSuite Ecommerce solutions from SpireNest. Complete ecommerce implementation and integration services." />
        <meta name="keywords" content="NetSuite ecommerce, ecommerce solutions, online business, USA, SpireNest" />
      </Helmet>
      {/* Hero Section */}
      <section className="ecommerce-hero py-5 position-relative" style={{minHeight: '92vh'}}>
        <div className="ecommerce-hero-bg"></div>
        <Container className="position-relative">
          <motion.div
            ref={heroRef}
            initial="hidden"
            animate={heroInView ? "visible" : "hidden"}
            variants={staggerContainer}
            className="text-center py-5"
          >
            <motion.span variants={fadeIn} className="hero-badge bg-light text-black">
              E-COMMERCE SOLUTION
            </motion.span>
            <motion.h1 variants={fadeIn} className="fw-bold display-3 mb-4 text-white">
              NetSuite For <span style={{ color:'#f97316 ',
                    textShadow: '0 0 10px rgba(249, 115, 22, 0.5' }} >Ecommerce</span>
            </motion.h1>
            <motion.p variants={fadeIn} className="lead text-white-75 mb-5">
              NetSuite Ecommerce is a cloud-based ecommerce solution with inventory management, order processing, CRM integration, and built-in business management capabilities.
            </motion.p>
            <motion.div variants={fadeIn}>
              <Link to="/contact" className="btn btn-light btn-lg px-4 py-3 rounded-pill fw-semibold text-decoration-none">
                Talk to Our Experts
              </Link>
            </motion.div>
          </motion.div>
        </Container>
      </section>

      {/* Why NetSuite E-Commerce Section */}
      <section className="why-ecommerce py-5 bg-light">
        <Container>
          <motion.div
            ref={whyRef}
            initial="hidden"
            animate={whyInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeIn} className="text-center mb-5">
              <h2 className="fw-bold display-5 mb-3">Why NetSuite E-Commerce</h2>
              <p className="lead text-muted">
                NetSuite SuiteCommerce is a powerful ecommerce solution designed to help businesses 
                boost their online sales and streamline operations. Built for both B2B and B2C companies.
              </p>
            </motion.div>
            
            <Row className="justify-content-center">
              <Col lg={8}>
                <motion.div variants={staggerContainer} className="why-list">
                  {whyChooseItems.map((item, index) => (
                    <motion.div
                      key={index}
                      variants={fadeIn}
                      className="why-item d-flex align-items-start mb-3"
                    >
                      <div className="why-bullet me-3">
                        <i className="bi-check-circle-fill text-primary"></i>
                      </div>
                      <p className="mb-0 text-dark">{item}</p>
                    </motion.div>
                  ))}
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Core Features Section */}
      <section className="core-features py-5">
        <Container>
          <motion.div
            ref={featuresRef}
            initial="hidden"
            animate={featuresInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeIn} className="text-center mb-5">
              <h2 className="fw-bold display-5 mb-3">Core Features of NetSuite Ecommerce</h2>
            </motion.div>
            
            {/* Desktop Circular Diagram */}
            <div className="features-diagram position-relative d-none d-lg-block">
              <div className="central-hub">
                <div className="central-circle">
                  <h5 className="text-white fw-bold mb-0">B2C E-Commerce</h5>
                  <p className="text-white-50 small mb-0">Comprehensive online retail solution</p>
                </div>
              </div>
              
              {coreFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  variants={fadeIn}
                  className={`feature-node feature-${index + 1}`}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="feature-circle">
                    <div className="feature-icon mb-2">
                      <i className={`${feature.icon} text-primary`}></i>
                    </div>
                    <h6 className="fw-bold mb-2" style={{fontSize: '0.95rem', lineHeight: '1.2'}}>{feature.title}</h6>
                    <p className="mb-0" style={{fontSize: '0.75rem', lineHeight: '1.3'}}>{feature.description}</p>
                  </div>
                </motion.div>
              ))}
              
              {/* Connection lines with improved positioning */}
              <svg className="connection-lines" viewBox="0 0 1200 600">
                <line x1="600" y1="300" x2="250" y2="120" stroke="rgba(0, 102, 204, 0.3)" strokeWidth="2"/>
                <line x1="600" y1="300" x2="950" y2="120" stroke="rgba(0, 102, 204, 0.3)" strokeWidth="2"/>
                <line x1="600" y1="300" x2="1050" y2="210" stroke="rgba(0, 102, 204, 0.3)" strokeWidth="2"/>
                <line x1="600" y1="300" x2="950" y2="480" stroke="rgba(0, 102, 204, 0.3)" strokeWidth="2"/>
                <line x1="600" y1="300" x2="250" y2="480" stroke="rgba(0, 102, 204, 0.3)" strokeWidth="2"/>
                <line x1="600" y1="300" x2="150" y2="210" stroke="rgba(0, 102, 204, 0.3)" strokeWidth="2"/>
              </svg>
            </div>
            
            {/* Mobile Grid Fallback */}
            <Row className="g-4 d-lg-none">
              {coreFeatures.map((feature, index) => (
                <Col md={6} key={index}>
                  <motion.div
                    variants={fadeIn}
                    whileHover={{ y: -5 }}
                  >
                    <Card className="h-100 border-0 shadow-sm feature-mobile-card">
                      <Card.Body className="text-center p-4">
                        <div className="feature-icon mx-auto mb-3">
                          <i className={`${feature.icon} fs-2 text-primary`}></i>
                        </div>
                        <h5 className="fw-bold mb-3">{feature.title}</h5>
                        <p className="text-muted mb-0">{feature.description}</p>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section py-5 bg-light">
        <Container>
          <motion.div
            ref={benefitsRef}
            initial="hidden"
            animate={benefitsInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeIn} className="text-center mb-5">
              <h2 className="fw-bold display-5 mb-3">Benefits of NetSuite Ecommerce</h2>
            </motion.div>
            
            <Row className="g-4">
              {benefits.map((benefit, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div
                    variants={fadeIn}
                    whileHover={{ y: -5 }}
                  >
                    <Card className="h-100 border-0 shadow-sm benefit-card bg-light">
                      <Card.Body className="text-center p-4">
                        <div className="benefit-icon mx-auto mb-3">
                          <i className={`${benefit.icon} fs-2 text-primary`}></i>
                        </div>
                        <h5 className="fw-bold mb-3 text-black">{benefit.title}</h5>
                        <p className="text-muted mb-0">{benefit.description}</p>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Understanding NetSuite E-Commerce */}
      <section className="understanding-section py-5 bg-dark text-white">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
          >
            <Row className="align-items-center">
              <Col lg={12}>
                <motion.div variants={fadeIn} className="text-center">
                  <h2 className="fw-bold display-5 mb-4">Understanding NetSuite E-Commerce</h2>
                  <p className="lead mb-0">
                    NetSuite E-Commerce empowers you to provide customers with a fast and captivating experience they seek while you streamline 
                    operations. Gain operational advantage with NetSuite E-commerce which brings together B2B and B2C ecommerce solutions into 
                    one unified platform. With e-commerce is a unified integration of E-commerce, ERP, inventory and order management, marketing, 
                    customer service, and financials. As a comprehensive business suite, organizations can build and manage their entire business 
                    for a fluid, comprehensive, and unified solution.
                  </p>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section py-5">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true }}
          >
            <Row className="align-items-center">
              <Col lg={6}>
                <motion.div variants={fadeIn}>
                  <h2 className="fw-bold display-5 mb-4">Why Choose Us</h2>
                  <div className="why-choose-list">
                    {whyChooseUs.map((item, index) => (
                      <div key={index} className="why-choose-item-simple mb-4">
                        <h6 className="fw-bold mb-2 text-primary">{item.title}:</h6>
                        <p className="text-muted mb-0">{item.description}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </Col>
              <Col lg={6}>
                <motion.div variants={fadeIn} className="text-center">
                  <div className="why-choose-illustration">
                    <i className="bi-question-circle-fill text-primary" style={{fontSize: '8rem', opacity: 0.3}}></i>
                  </div>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5 bg-primary text-white">
        <Container>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center py-4"
          >
            <h2 className="fw-bold mb-4 display-5">Ready to Transform Your E-commerce?</h2>
            <p className="lead mb-4">
              Start your journey with NetSuite's comprehensive e-commerce solutions
            </p>
            <div className="d-flex justify-content-center gap-3">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link to="/contact" className="btn btn-light btn-lg px-4 py-3 rounded-pill fw-semibold text-decoration-none">
                  Get Started Now
                </Link>
              </motion.div>
              {/* <motion.button
                className="btn btn-outline-light btn-lg px-4 py-3 rounded-pill fw-semibold"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Contact Us
              </motion.button> */}
            </div>
          </motion.div>
        </Container>
      </section>
      <FAQSection />
    </div>
  );
};

export default NetSuiteEcommercePage;